import { TableCell, TableRow } from '@heroui/react';
import React from 'react';
import { FiPercent } from 'react-icons/fi';

import {
  LoadingTable,
  type LoadingTableColumn,
} from '../../../../../../../components/Table';
import { type TaxJurisdictionRow } from '../types';
import { ABSENT_VALUE } from '../utils/taxJurisdictionMapper';

interface TaxJurisdictionTableProps {
  jurisdictions: TaxJurisdictionRow[];
  isLoading: boolean;
  /** Opens the read-only rate detail view for the clicked row. */
  onSelectJurisdiction: (jurisdiction: TaxJurisdictionRow) => void;
}

const columns: LoadingTableColumn[] = [
  { label: 'Code', className: 'pl-6 min-w-32' },
  { label: 'Country', className: 'min-w-28' },
  { label: 'Subdivision', className: 'hidden md:table-cell min-w-32' },
  { label: 'Rates', className: 'min-w-20' },
];

export const TaxJurisdictionTable: React.FC<TaxJurisdictionTableProps> = ({
  jurisdictions,
  isLoading,
  onSelectJurisdiction,
}) => {
  const renderRow = (jurisdiction: TaxJurisdictionRow) => (
    <TableRow
      key={jurisdiction.id}
      className="cursor-pointer transition-colors hover:bg-default-50 focus-visible:outline-2 focus-visible:outline-primary"
      tabIndex={0}
      aria-label={`View rates for ${jurisdiction.code}`}
      onClick={() => onSelectJurisdiction(jurisdiction)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          // Space would otherwise scroll the page; Enter is a no-op default.
          event.preventDefault();
          onSelectJurisdiction(jurisdiction);
        }
      }}
    >
      <TableCell className="pl-6">
        <span className="font-medium text-foreground">{jurisdiction.code}</span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-default-600">
          {jurisdiction.countryCode}
        </span>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <span className="text-sm text-default-600">
          {jurisdiction.subdivisionCode ?? ABSENT_VALUE}
        </span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-foreground">
          {jurisdiction.rates.length}
        </span>
      </TableCell>
    </TableRow>
  );

  return (
    <LoadingTable
      columns={columns}
      data={jurisdictions}
      renderRow={renderRow}
      isLoading={isLoading}
      isEmpty={jurisdictions.length === 0}
      rowCount={5}
      ariaLabel="Tax jurisdictions table"
      loadingMessage="Loading tax jurisdictions..."
      emptyState={{
        title: 'No tax jurisdictions',
        description: 'This distributor has no tax jurisdictions configured.',
        icon: FiPercent,
      }}
    />
  );
};
